<template>
  <div class="min-h-screen bg-slate-50 flex items-center justify-center px-4">
    <div class="max-w-md w-full">
      <!-- Loading State -->
      <div v-if="loading" class="bg-white rounded-lg shadow-lg p-8 text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p class="text-gray-600">Validating invitation...</p>
      </div>

      <!-- Invalid Invite -->
      <div v-else-if="error" class="bg-white rounded-lg shadow-lg p-8 text-center">
        <PhXCircle :size="64" class="text-red-500 mx-auto mb-4" />
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        <router-link
          to="/login"
          class="inline-block px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          Go to Login
        </router-link>
      </div>

      <!-- Signup Success -->
      <div v-else-if="signupSuccess" class="bg-white rounded-lg shadow-lg p-8 text-center">
        <PhCheckCircle :size="64" class="text-green-500 mx-auto mb-4" />
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
        <p class="text-gray-600 mb-6">{{ signupMessage }}</p>
        <router-link
          to="/login"
          class="inline-block px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          Go to Login
        </router-link>
      </div>

      <!-- Valid Invite - Show Details -->
      <div v-else-if="inviteDetails && !signupSuccess" class="bg-white rounded-lg shadow-lg p-8">
        <div class="text-center mb-6">
          <PhCheckCircle :size="64" class="text-green-500 mx-auto mb-4" />
          <h2 class="text-2xl font-bold text-gray-900 mb-2">You're Invited!</h2>
          <p class="text-gray-600">
            You've been invited to join <strong>{{ inviteDetails.organization_name }}</strong>
          </p>
        </div>

        <div class="bg-gray-50 rounded-md p-4 mb-6">
          <dl class="space-y-2">
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-600">Organization:</dt>
              <dd class="text-sm text-gray-900">{{ inviteDetails.organization_name }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-600">Invited by:</dt>
              <dd class="text-sm text-gray-900">{{ inviteDetails.invited_by }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-600">Role:</dt>
              <dd class="text-sm text-gray-900 capitalize">{{ inviteDetails.role }}</dd>
            </div>
          </dl>
        </div>

        <!-- Sign Up Form -->
        <div v-if="!existingUser">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Create Your Account</h3>
          <form @submit.prevent="handleSignup" class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                v-model="email"
                type="email"
                id="email"
                required
                :disabled="!!inviteDetails.email"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                :class="{ 'bg-gray-100': !!inviteDetails.email }"
              />
            </div>

            <div>
              <label for="display_name" class="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                v-model="displayName"
                type="text"
                id="display_name"
                required
                placeholder="John Doe"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                v-model="password"
                type="password"
                id="password"
                required
                minlength="6"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label for="confirm_password" class="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                v-model="confirmPassword"
                type="password"
                id="confirm_password"
                required
                minlength="6"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div v-if="signupError" class="text-red-600 text-sm">
              {{ signupError }}
            </div>

            <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p class="text-sm text-blue-800 flex items-center">
                <PhSparkle class="inline w-4 h-4 text-yellow-500 mr-1" weight="fill" /> No credit card required! You'll use your organization's shared spark balance.
              </p>
            </div>

            <button
              type="submit"
              :disabled="submitting"
              class="w-full px-4 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ submitting ? 'Creating Account...' : 'Create Account & Join' }}
            </button>
          </form>

          <p class="text-center text-sm text-gray-600 mt-4">
            Already have an account?
            <button
              @click="existingUser = true"
              class="text-primary hover:text-primary-dark font-medium"
            >
              Sign in instead
            </button>
          </p>
        </div>

        <!-- Sign In Form for Existing Users -->
        <div v-else>
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Sign In to Accept Invitation</h3>
          <p class="text-sm text-gray-600 mb-4">
            Sign in with your existing account to join {{ inviteDetails.organization_name }}.
          </p>
          <router-link
            :to="`/login?invite=${inviteCode}`"
            class="block w-full px-4 py-3 bg-primary text-white text-center rounded-md hover:bg-primary-dark transition-colors"
          >
            Sign In & Join
          </router-link>
          
          <button
            @click="existingUser = false"
            class="block w-full mt-3 px-4 py-3 text-gray-700 text-center border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Create New Account Instead
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { PhCheckCircle, PhXCircle, PhSparkle } from "@phosphor-icons/vue";

interface InviteDetails {
  organization_name: string;
  organization_id: string;
  invited_by: string;
  role: string;
  email?: string;
  expires_at: string;
}

const router = useRouter();
const route = useRoute();

// State
const loading = ref(true);
const error = ref('');
const inviteCode = ref('');
const inviteDetails = ref<InviteDetails | null>(null);
const existingUser = ref(false);
const submitting = ref(false);
const signupError = ref('');
const signupSuccess = ref(false);
const signupMessage = ref('');

// Form fields
const email = ref('');
const displayName = ref('');
const password = ref('');
const confirmPassword = ref('');

// Validate the invite code
async function validateInvite() {
  const code = route.query.code as string;
  
  if (!code) {
    error.value = 'No invitation code provided';
    loading.value = false;
    return;
  }

  inviteCode.value = code;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/auth/validate-invite?code=${code}`
    );

    const data = await response.json();

    if (response.ok && data.success) {
      inviteDetails.value = data.data;
      // Pre-fill email if specified in invite
      if (data.data.email) {
        email.value = data.data.email;
      }
    } else {
      error.value = data.error || 'Invalid or expired invitation';
    }
  } catch (err) {
    console.error('Error validating invite:', err);
    error.value = 'Failed to validate invitation';
  } finally {
    loading.value = false;
  }
}

// Handle signup with invite
async function handleSignup() {
  if (password.value !== confirmPassword.value) {
    signupError.value = 'Passwords do not match';
    return;
  }

  submitting.value = true;
  signupError.value = '';

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/auth/signup`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.value,
          password: password.value,
          display_name: displayName.value,
          invite_code: inviteCode.value
        })
      }
    );

    const data = await response.json();

    if (response.ok && data.success) {
      // Check if session exists (it may be null if email confirmation is required)
      if (data.data.session) {
        // Set auth cookie
        const cookieData = {
          token: data.data.session.access_token,
          user_id: data.data.user.id,
          email: data.data.user.email
        };

        const encodedData = btoa(JSON.stringify(cookieData));
        document.cookie = `ha_token=${encodedData}; Domain=.happyalien.ai; Path=/; Secure; SameSite=Lax; Max-Age=${7 * 24 * 60 * 60}`;

        // For localhost development
        if (window.location.hostname === 'localhost') {
          document.cookie = `ha_token=${encodedData}; Path=/; SameSite=Lax; Max-Age=${7 * 24 * 60 * 60}`;
        }

        // Redirect to dashboard
        router.push('/');
      } else {
        // No session - likely needs email confirmation
        signupSuccess.value = true;
        signupMessage.value = 'Account created successfully! Please check your email to confirm your account before signing in.';
      }
    } else {
      signupError.value = data.error || 'Failed to create account';
    }
  } catch (err) {
    console.error('Error during signup:', err);
    signupError.value = 'An unexpected error occurred';
  } finally {
    submitting.value = false;
  }
}

onMounted(() => {
  validateInvite();
});
</script>

<style scoped>
.text-primary {
  color: #8b5cf6;
}

.bg-primary {
  background-color: #8b5cf6;
}

.hover\:bg-primary-dark:hover {
  background-color: #7c3aed;
}

.text-primary-dark {
  color: #7c3aed;
}

.hover\:text-primary-dark:hover {
  color: #7c3aed;
}
</style>